import { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import type { ReactNode } from 'react';
import type {
  GetMaintenanceMessagesMarblesQuery,
  GetMaintenanceMessagesQuery,
  MaintenanceMessageSeverity,
} from 'types/__generated__';

import { AppStatusBar } from './AppStatusBar/AppStatusBar';

import { GET_MAINTENANCE_MESSAGES } from 'graphql/queries/getMaintenanceMessages';
import { GET_MAINTENANCE_MESSAGES_MARBLES } from 'graphql/queries/getMaintenanceMessagesMarbles';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const POLL_INTERVAL = 5 * 1000 * 60; // 5 minutes

interface AppStatusProps {
  children: ReactNode;
}

type MaintenanceMessage = {
  message: string;
  severity: MaintenanceMessageSeverity;
};

export function AppStatus({ children }: AppStatusProps) {
  const isOvaEnabled = Boolean(window._env.ovaEnabled) || false;

  const barRef = useRef<HTMLDivElement>(null);
  const [appStatusBarHeight, setAppStatusBarHeight] = useState(0);
  const [maintenanceMessages, setMaintenanceMessages] = useState<MaintenanceMessage[] | null>(null);

  const [magnifierQuery] = useLazyQuery<GetMaintenanceMessagesQuery>(GET_MAINTENANCE_MESSAGES, {
    pollInterval: POLL_INTERVAL,
  });
  const [marblesQuery] = useLazyQuery<GetMaintenanceMessagesMarblesQuery>(GET_MAINTENANCE_MESSAGES_MARBLES, {
    pollInterval: POLL_INTERVAL,
  });

  useEffect(() => {
    async function asyncQuery() {
      try {
        if (isOvaEnabled) {
          await magnifierQuery({
            onCompleted(data) {
              const mappedData = data.maintenanceMessages.map(({ message, severity }) => ({
                message,
                severity,
              }));
              setMaintenanceMessages(mappedData);
            },
          });
        } else {
          await marblesQuery({
            onCompleted(data) {
              const mappedData = data.marblesMaintenanceMessages.map(({ message, severity }) => ({
                message,
                severity,
              }));
              setMaintenanceMessages(mappedData);
            },
          });
        }
      } catch (exception) {
        const error = exception as DOMException;

        if (error.name !== 'AbortError') {
          throw error;
        }
      }
    }
    asyncQuery();
    setAppStatusBarHeight(barRef.current?.clientHeight || 0);
  }, [isOvaEnabled, magnifierQuery, marblesQuery]);

  return maintenanceMessages ? (
    <>
      {maintenanceMessages.map(({ message, severity }, key) => (
        <AppStatusBar
          severity={severity}
          message={message}
          key={key}
          appStatusBarHeight={appStatusBarHeight}
          ref={barRef}
        />
      ))}
      {children}
    </>
  ) : null;
}
